const WhyCreditGO = () => {
  const leftPoints = [
    {
      title: 'Beyond the Snapshot',
      description:
        'Traditional credit systems rely on DSCR\u2014a single ratio from your current financial position. CreditGO analyzes your entire financial story, using years of revenue, expenses and growth patterns to understand where your business is headed.',
      icon: (
        <svg className="w-5 h-5 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
    },
    {
      title: 'Predictive, Not Reactive',
      description:
        'While conventional lenders look backward, we look forward. Our algorithms identify trends, detect momentum and forecast your financial trajectory. A business on an upward path deserves recognition.',
      icon: (
        <svg className="w-5 h-5 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
  ];

  const rightPoints = [
    {
      title: 'The Complete Picture',
      description:
        'DSCR ignores context. A temporary dip, a strategic investment, a seasonal fluctuation\u2014traditional systems penalize what they don\u2019t understand. CreditGO weighs multiple financial dimensions simultaneously.',
      icon: (
        <svg className="w-5 h-5 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      title: 'Built for Growth',
      description:
        'Creditworthiness should reflect potential, not just history. CreditGO gives emerging businesses a fair chance to access the capital they need to scale\u2014backed by rigorous analysis.',
      icon: (
        <svg className="w-5 h-5 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="why-creditgo" className="min-h-screen py-20 bg-white flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why CreditGO</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            A smarter approach to credit eligibility — powered by data, driven by insight.
          </p>
        </div>

        {/* Content: Left points | BANK | Right points */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-end">
          {/* Left Points */}
          <div className="space-y-10">
            {leftPoints.map((point) => (
              <div key={point.title} className="text-right lg:text-right text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-primary-green/10 rounded-lg mb-3 lg:ml-auto">
                  {point.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>

          {/* Center - Bank Building Illustration */}
          <div className="flex justify-center">
            <div className="relative">
              <svg
                className="w-64 h-72"
                viewBox="0 0 260 290"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Ground shadow */}
                <ellipse cx="130" cy="280" rx="120" ry="8" fill="#e5e7eb" />

                {/* Main building body */}
                <rect x="40" y="100" width="180" height="170" rx="4" fill="#2c6e49" />

                {/* Pediment triangle */}
                <polygon points="20,105 130,30 240,105" fill="#1e5738" />
                <polygon points="55,105 130,50 205,105" fill="#2c6e49" stroke="#3fa34d" strokeWidth="1" />

                {/* Roof line */}
                <rect x="20" y="100" width="220" height="8" rx="2" fill="#1e5738" />

                {/* Pillars */}
                <rect x="65" y="112" width="14" height="150" rx="2" fill="#f8fafc" />
                <rect x="105" y="112" width="14" height="150" rx="2" fill="#f8fafc" />
                <rect x="145" y="112" width="14" height="150" rx="2" fill="#f8fafc" />
                <rect x="185" y="112" width="14" height="150" rx="2" fill="#f8fafc" />

                {/* Pillar capitals */}
                <rect x="61" y="108" width="22" height="8" rx="2" fill="#e2e8f0" />
                <rect x="101" y="108" width="22" height="8" rx="2" fill="#e2e8f0" />
                <rect x="141" y="108" width="22" height="8" rx="2" fill="#e2e8f0" />
                <rect x="181" y="108" width="22" height="8" rx="2" fill="#e2e8f0" />

                {/* Pillar bases */}
                <rect x="61" y="258" width="22" height="6" rx="1" fill="#e2e8f0" />
                <rect x="101" y="258" width="22" height="6" rx="1" fill="#e2e8f0" />
                <rect x="141" y="258" width="22" height="6" rx="1" fill="#e2e8f0" />
                <rect x="181" y="258" width="22" height="6" rx="1" fill="#e2e8f0" />

                {/* Steps */}
                <rect x="30" y="264" width="200" height="8" rx="2" fill="#3fa34d" />
                <rect x="22" y="272" width="216" height="8" rx="2" fill="#2c6e49" />

                {/* Dollar sign in pediment */}
                <circle cx="130" cy="78" r="16" fill="#3fa34d" />
                <text x="130" y="84" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">$</text>

                {/* Flag on top */}
                <line x1="130" y1="15" x2="130" y2="30" stroke="#1e5738" strokeWidth="2" />
                <polygon points="130,15 148,21 130,27" fill="#3fa34d" />
              </svg>

              {/* Decorative glow */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-48 h-8 bg-primary-green/10 rounded-full blur-xl"></div>
            </div>
          </div>

          {/* Right Points */}
          <div className="space-y-10">
            {rightPoints.map((point) => (
              <div key={point.title} className="text-left lg:text-left text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-primary-green/10 rounded-lg mb-3">
                  {point.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyCreditGO;
